<template>
  <div v-if="!isLoading">
    <b-container>
      <div class="mt-4 mb-4 d-flex align-items-start justify-content-between">
        <!-- Titre et boutons -->
        <div>
          <h1 class="page-title">{{ $t('Tutorial') }}</h1>
          <h2 class="page-subtitle">{{ $t('Total') }} : {{ totalCount }}</h2>
        </div>

        <div>
          <b-button class="mr-2" variant="outline-primary" @click="clickOnUpdateOrdre" v-if="isAdmin">
            <list-numbers-icon class="mr-1" size="18"/>
            {{ $t('tutorial.manage.order') }}
          </b-button>

          <b-button variant="primary" @click="clickOnCreateBtn" v-if="isAdmin">
            <plus-icon class="mr-1" size="18"/>
            {{ $t('Add') }} {{ $t('a.e') }} {{ $t('video') }}
          </b-button>
        </div>
      </div>
      <template v-if="totalCount === 0">
        <div class="empty-state-container">
          <div class="text-center mb-4">
            <img :src="require(`@/assets/images/APRILYOS/empty-table.png`)" alt="no result" class="empty-state-img">
          </div>
          <div class=" text-center no-result-text">
            <div class="no-result-title mb-2">{{ $t('tutorial.emptyTitle') }}</div>
            <div class="main-color-text">{{ $t('tutorial.emptyText') }}</div>
            <b-button class="my-2" variant="primary" @click="clickOnCreateBtn" v-if="isAdmin">
              {{ $t('tutorial.emptyButton') }}
            </b-button>
          </div>
        </div>
      </template>
      <!-- Liste des tutoriels -->
      <template v-else>
        <div class="row">
          <template v-for="tutorial in tutorials">
            <div class="col-sm-1 col-md-4 col-lg-4 my-2">
              <b-card class="cursor-pointer" @click.stop="openVideo(tutorial)">
                <div v-if="tutorial.preview" class="mb-3 picture-container">
                  <div class="overlay">
                  </div>
                  <svg v-if="!isAdmin" class="icon icon-tabler icons-tabler-filled icon-tabler-player-play overlay-icon"
                       fill="currentColor" height="48" viewBox="0 0 24 24"
                       width="48" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0h24v24H0z" fill="none" stroke="none"/>
                    <path d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z"/>
                  </svg>
                  <img :src="tutorial.preview ? getFileUrl(tutorial.preview.fileUrl) :''" alt=""
                       class="result-img"/>
                </div>
                <div v-else class="mb-3 picture-container">
                  <div class="overlay">
                  </div>
                  <svg v-if="!isAdmin" class="icon icon-tabler icons-tabler-filled icon-tabler-player-play overlay-icon" fill="currentColor" height="48" viewBox="0 0 24 24"
                       width="48" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0h24v24H0z" fill="none" stroke="none"/>
                    <path d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z"/>
                  </svg>
                  <img :src="require(`@/assets/images/APRILYOS/sidebar-logo.png`)" alt="No picture" class="result-img">
                </div>

                <div class="row justify-content-between ml-2 mr-2 px-0">
                  <div style="max-width: 80%;min-width: 150px">
                    <b-link class="main-color-text bold-text level-two-title">
                      {{ tutorial.title }}
                    </b-link>
                  </div>
                </div>

                <div class="row justify-content-sm-between ml-2 mr-2 px-0">
                  <div>{{ formatDuration(tutorial.duration) }}</div>
                  <div class="tertiary-color small-text bold-text my-1">
                    {{ $t('N°') + tutorial.ordre }}
                  </div>
                </div>
              </b-card>
            </div>
          </template>
        </div>
      </template>

      <!-- Modale de lecture vidéo -->
      <b-modal
        ref="videoModal"
        hide-footer
        size="auto"
        id="videoModal"
        :title="currentVideoName"
        @hide="closeVideo"
        :class="modalSizeClass"
        :content-class="modalSizeClass"
      >
        <video
          v-if="currentVideoBlobUrl"
          id="videoElement"
          ref="videoElement"
          autoplay
          controls
          playsInLine
          controlsList="nodownload"
          disablePictureInPicture
          @loadedmetadata="adjustModalSize"
        >
          <source :src="currentVideoBlobUrl" type="video/mp4">
        </video>
      </b-modal>

    </b-container>
  </div>
</template>


<script>

import api from "../../services/api";
import {formatDuration, intervalToDuration} from "date-fns";
import {fr} from 'date-fns/locale';

export default {

  name: 'TutorialIndex',

  components: {},

  data() {
    return {
      isCollaborator: this.$store.getters.isCollaborator,
      isCollaboratorPlus: this.$store.getters.isCollaboratorPlus,
      isLoading: false,
      totalCount: 0,
      allowCreate: true,
      tutorials: [],
      currentVideoBlobUrl: null, // URL Blob de la vidéo en cours
      currentVideoName: null, // URL de la vidéo en cours de lecture
      isAdmin: this.$store.getters.isAdmin,
      modalSizeClass:''
    }
  },

  async mounted() {
    await this.fetchItems()
  },

  methods: {


    async openVideo(tutorial) {
      if (!this.isAdmin) {
        this.currentVideoName = tutorial.title;
        try {
          const response = await api.get(`/tutorial/showVideoFromWeb/${tutorial.video.id}`, {
            responseType: "blob",
          });
          this.currentVideoBlobUrl = URL.createObjectURL(response.data);
          this.$refs.videoModal.show();
        } catch (error) {
          console.error("Erreur lors de la récupération de la vidéo :", error);
        }
      } else {
        this.$router.push({ name: 'TutorialShow', params: { tutorialId: tutorial.id } });
      }
    },

    closeVideo() {
      if (this.currentVideoBlobUrl) {
        URL.revokeObjectURL(this.currentVideoBlobUrl);
      }
      this.currentVideoBlobUrl = null;
      this.currentVideoName = null;
    },

    adjustModalSize() {
      const videoElement = this.$refs.videoElement;
      const modalDialog = this.$refs.videoModal.$refs.dialog; // Accès direct au `.modal-dialog`

      if (videoElement && modalDialog) {
        const videoWidth = videoElement.videoWidth + 40;
        const videoHeight = videoElement.videoHeight;

        // Applique les styles dynamiques directement au `.modal-dialog`
        modalDialog.style.width = `${videoWidth}px`;
        modalDialog.style.maxWidth = `${videoWidth}px`;
        modalDialog.style.height = `${videoHeight}px`;
        modalDialog.style.maxHeight = `${videoHeight}px`;

      }
    },


    formatDuration(durationMs) {
      // Utilise intervalToDuration pour obtenir la durée décomposée en heures, minutes, et secondes
      const duration = intervalToDuration({start: 0, end: durationMs});

      // Utilise formatDuration pour formater en HH:MM:SS
      return formatDuration(duration, {format: ['hours', 'minutes', 'seconds'], locale: fr});
    },


    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },

    getFileUrl(itemUrl) {
      return process.env.SERVER_URL + itemUrl
    },

    fetchItems() {
      this.isLoading = true
      api
        .get('/tutorial')
        .then(response => {
          this.tutorials = response.data
          this.totalCount = this.tutorials.length
          this.isLoading = false
        })
    },

    clickOnCreateBtn() {
      this.$router.push({
        name: 'TutorialCreate',
      })
    },

    clickOnUpdateOrdre(){
      this.$router.push({
        name: 'TutorialUpdateOrdre',
      })
    },


  }
}
</script>

<style scoped>

.tertiary-color {
  color: var(--tertiary-text-color);
}

.card-body {
  padding: 0 0 0.5rem;
}

.card {
  border-radius: 10px;
  padding: 0;
}


.empty-state-container {
  height: 70%;
  margin-top: 15%;
  margin-bottom: 15%;
}

.empty-state-img {
  max-width: 220px;
}

.no-result-img {
  max-width: 135px;
  max-height: 83px;
}

.picture-container {
  border-radius: 10px 10px 0 0;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.picture-container .result-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}






.overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: absolute;
  height: 200px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 10px 10px 0 0;
  font-family: Agbalumo-Regular, "Arial", sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 22px;
  z-index: 1;
  opacity: 0.2;
}

.overlay-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: absolute;
  color: white;
  border-radius: 10px 10px 0 0;
  font-family: Agbalumo-Regular, "Arial", sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 22px;
  z-index: 2;
}

.no-result-title {
  font-family: "Montserrat-Bold", "Arial", sans-serif;
  color: var(--main-color-text);
  font-weight: 400;
}

.custom-width-modal{
  max-width: 2000px!important;
}



.video-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-modal-1280-720  {
  width: 1280px !important;
  height: 720px !important;
}

.video-modal-1280-720 .modal-dialog {
  width: 1280px !important;
  height: 720px !important;
}
.video-modal-1280-720 .modal-content {
  width: 1280px !important;
  height: 720px !important;
}

.video-modal-640-480 .modal-dialog {
  width: 640px !important;
  height: 480px !important;
}

</style>



// WEBPACK FOOTER //
// src/views/tutorial/TutorialIndex.vue